/**
 * Chart.js Threshold Bands Plugin
 *
 * Paints horizontal background bands keyed to Y-axis value ranges — a visual
 * guideline (e.g. green / amber / red zones) behind the plotted data. The bands
 * are a reference, not data: keep the fill alpha low so lines stay dominant.
 *
 * Plugin options (chart.options.plugins.thresholdBands):
 *   bands: { from: number; color: string; label?: string }[]
 *     Sorted ascending by `from`. Each band fills from its own `from` up to the
 *     next band's `from`; the last band fills up to the top of the chart area.
 *     The first band's `from` may be -Infinity to mean "everything below".
 *   (label is carried for future on-canvas rendering; this plugin does not draw text yet.)
 *
 * Axis extension is NOT done here — the component computes suggestedMin/Max via
 * thresholdBandAxisHints() and merges them into scales.y before the chart builds.
 */

export interface ThresholdBand {
  /** Lower Y bound of this band (inclusive). -Infinity allowed on the first band. */
  from: number;
  /** Fill colour — use a low-alpha rgba so plotted data reads on top. */
  color: string;
  /** Optional zone name. Not rendered yet; reserved for on-canvas labels. */
  label?: string;
}

export interface ThresholdBandConfig {
  bands: ThresholdBand[];
  /** Grow the Y axis (suggestedMin/Max) so every band is visible even when the
   *  data sits far below the thresholds. Default true. */
  extendAxis?: boolean;
}

/** Sorted copy of the bands, ascending by `from`. */
function sortedBands(bands: ThresholdBand[]): ThresholdBand[] {
  return [...bands].sort((a, b) => a.from - b.from);
}

/**
 * Suggested Y-axis bounds so all bands are visible. `suggestedMax` (not `max`)
 * so real data exceeding the top band still grows the axis rather than clipping.
 *   suggestedMax = topBand.from * 1.1   (10% headroom above the last "from")
 *   suggestedMin = lowest finite `from`, never above 0
 */
export function thresholdBandAxisHints(
  bands: ThresholdBand[],
): { suggestedMin: number; suggestedMax: number } | null {
  if (!bands?.length) return null;
  const sorted = sortedBands(bands);
  const finiteFroms = sorted.map((b) => b.from).filter((f) => Number.isFinite(f));
  if (!finiteFroms.length) return null;

  const top = finiteFroms[finiteFroms.length - 1];
  const bottom = finiteFroms[0];

  const suggestedMax = top > 0 ? top * 1.1 : top + Math.abs(top) * 0.1 + 1;
  const suggestedMin = Math.min(0, bottom);

  return { suggestedMin, suggestedMax };
}

export const ThresholdBandsPlugin = {
  id: 'thresholdBands',

  beforeDatasetsDraw(chart: any) {
    const opts: ThresholdBandConfig | undefined = chart.options?.plugins?.thresholdBands;
    if (!opts?.bands?.length) return;

    const yScale = chart.scales?.['y'];
    const area = chart.chartArea;
    if (!yScale || !area) return;

    const bands = sortedBands(opts.bands);
    const ctx = chart.ctx;

    ctx.save();
    // Clip to the plotting area so bands never bleed over axes / padding.
    ctx.beginPath();
    ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
    ctx.clip();

    for (let i = 0; i < bands.length; i++) {
      const band = bands[i];
      const next = bands[i + 1];

      // pixel Y for this band's lower bound (or the axis bottom if -Infinity)
      const yLow = Number.isFinite(band.from)
        ? yScale.getPixelForValue(band.from)
        : area.bottom;
      // upper bound: the next band's `from`, or the chart-area top
      const yHigh = next ? yScale.getPixelForValue(next.from) : area.top;

      // getPixelForValue: larger value -> smaller pixel, so top = min, bottom = max
      const bandTop = Math.min(yLow, yHigh);
      const bandBottom = Math.max(yLow, yHigh);

      // nothing visible in-area
      if (bandBottom <= area.top || bandTop >= area.bottom) continue;

      const drawTop = Math.max(bandTop, area.top);
      const drawBottom = Math.min(bandBottom, area.bottom);

      ctx.fillStyle = band.color;
      ctx.fillRect(area.left, drawTop, area.right - area.left, drawBottom - drawTop);
    }

    ctx.restore();
  },
};
